// src/pages/admin/integrations/shopify/Shopify.tsx
import { useEffect, useState, type FC } from 'react';
import { SiShopify } from 'react-icons/si';
import PageLayout from '../../../components/layouts/PageLayout';
import CustomNavbar from '../../../components/common/CustomNavbar';
import { ShopifyTabKey } from '../../../types/integrations';
import { useShopify } from '../../../util/hooks/useShopify';
import ShopifyHeader from './ShopifyHeader';
import { TABS } from '../../../util/Constants';
import ConnectTab from './ConnectTab';
import FlowsTab from './FlowsTab';
import ActivityTab from './ActivityTab';
import SettingsTab from './SettingsTab';
import { useToast } from '../zoho-CMR/Zoho';


const Shopify: FC = () => {
  const { info, loading, error, reload, disconnect } = useShopify();
  const [tab, setTab] = useState<ShopifyTabKey | null>(null);
  const { toast: toastState, show: showToast } = useToast();

  // Default tab once info loads
  useEffect(() => {
    if (!info) return;
    setTab((t) => t ?? (info.status === 'CONNECTED' ? 'flows' : 'connect'));
  }, [info]);

  const connected = info?.status === 'CONNECTED';

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        'Disconnect Shopify? Scheduled messages will be cancelled.'
      )
    )
      return;
    await disconnect();
    
  };

  return (
    <PageLayout
      customNavbar={
        <CustomNavbar>
          <div className="flex w-max items-center gap-3">
            <SiShopify className="text-2xl text-[#5e8e3e]" />
            <span className="text-lg font-bold uppercase">Shopify</span>
          </div>
        </CustomNavbar>
      }
    >
      <section className="p-4 sm:p-6 bg-white rounded-lg">
        <div className="mx-auto">
          <ShopifyHeader info={info} onDisconnect={handleDisconnect} />

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {info?.lastError && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              {info.lastError}
            </div>
          )}

          {loading && !info && (
            <div className="h-48 rounded-xl bg-slate-200 animate-pulse" />
          )}

          {info && tab && (
            <>
              <nav className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
                {TABS.map(([k, l]) => {
                  const label =
                    k === 'connect' && connected ? 'Connection' : l;
                  return (
                    <button
                      key={k}
                      type="button"
                      role="tab"
                      aria-selected={tab === k}
                      onClick={() => setTab(k)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                        tab === k
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </nav>

              {tab === 'connect' && (
                <ConnectTab info={info} onDone={reload} toast={showToast} />
              )}
              {tab === 'flows' && (
                <FlowsTab connected={connected} toast={showToast} />
              )}
              {tab === 'activity' && <ActivityTab />}
              {tab === 'settings' && (
                <SettingsTab info={info} onSaved={reload} toast={showToast} />
              )}
            </>
          )}
        </div>
      </section>

      {toastState && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg ${
            toastState.kind === 'bad'
              ? 'bg-red-600 text-white'
              : 'bg-slate-900 text-white'
          }`}
          role="status"
        >
          {toastState.msg}
        </div>
      )}
    </PageLayout>
  );
};

export default Shopify;